import { useCallback, useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  closeModal,
  openModal,
  useAddOrUpdateRatingMutation,
  useRecipeQuickCommentsQuery,
} from "@chef/state-management";
import { useExtendedForm } from "@chef/hooks";
import { useAppDispatch } from "@chef/state-management/hooks";

import {
  ratingAndCommentSchema,
  RatingAndCommentFormValues,
} from "@chef/state-management";

import {
  getRatingCardState,
  getStateStatus,
  RatingAndCommentCardState,
} from "./util";
import { RecipeRating } from "./RecipeRating";
import { HaveNotCookedCheckbox } from "./DishMadeCheckbox";
import { RecipeComment } from "./RecipeComment";

import { intl } from "./RatingAndCommentContainer.Intl";

interface RecipeProps {
  recipeId: number;
  recipeName: string;
}

interface RatingAndCommentContainerProps {
  display?: boolean;
  id?: string;
  recipe: RecipeProps;
  ratingData: RatingAndCommentFormValues;
}

export const RatingAndCommentContainer = ({
  id,
  ratingData,
  recipe,
  display = false,
}: RatingAndCommentContainerProps) => {
  const dispatch = useAppDispatch();
  const {
    rating: initialRating,
    comment: initialComment,
    quickCommentIds: initialQuickCommentIds,
  } = ratingData;
  const { recipeId, recipeName } = recipe;

  const [userDefinedState, setUserDefinedState] =
    useState<
      Extract<
        RatingAndCommentCardState,
        "saved-expanded" | "saved-compressed" | "ongoing"
      >
    >();

  const formControls = useExtendedForm<RatingAndCommentFormValues>({
    resolver: yupResolver(ratingAndCommentSchema),
    mode: "all",
    defaultValues: {
      comment: initialComment || "",
      rating: initialRating || 0,
      quickCommentIds: initialQuickCommentIds || [],
    },
  });

  const [addOrUpdateRatingMutation] = useAddOrUpdateRatingMutation();

  const { data: recipeQuickCommentsQuery } = useRecipeQuickCommentsQuery(
    undefined,
    {
      skip: !display,
    },
  );

  const { getValues, setValue, reset } = formControls;

  const { rating, comment } = getValues();

  useEffect(() => {
    reset({
      comment: initialComment,
      rating: initialRating,
      quickCommentIds: initialQuickCommentIds,
    });
  }, [
    initialRating,
    initialComment,
    initialQuickCommentIds,
    recipeId,
    recipeQuickCommentsQuery,
    reset,
  ]);

  const ratingCardState = getRatingCardState({
    initialRating,
    comment,
    currentRating: rating,
  });

  const cardState = userDefinedState ?? ratingCardState;

  const hasNotInteractedWithRating = !rating || rating === 0;

  const lastClicked = useRef<number | null>(null);

  const onRatingClicked = useCallback(
    async (value: number) => {
      if (value === lastClicked.current) {
        return;
      }
      lastClicked.current = value;

      if (value === rating) {
        return;
      }

      setValue("rating", value);
      setUserDefinedState("ongoing");

      try {
        await addOrUpdateRatingMutation({
          rating: value,
          recipeId,
          quickCommentIds: getValues("quickCommentIds"),
          freeComment: getValues("comment"),
          isAnonymous: false,
        }).unwrap();
      } catch (e) {
        console.error(e);
      }
    },
    [rating, setValue, recipeId, getValues, addOrUpdateRatingMutation],
  );

  const handleTickedHaveNotMadeDish = async () => {
    setValue("rating", 0);
    if (initialRating !== 0) {
      try {
        await addOrUpdateRatingMutation({
          rating: 0,
          recipeId,
          quickCommentIds: [],
          freeComment: "",
          isAnonymous: false,
        }).unwrap();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onClickAddComment = () => setUserDefinedState("ongoing");

  const onToggleCommentExpanded = () => {
    setUserDefinedState((prevState) =>
      prevState === "saved-expanded" ? "saved-compressed" : "saved-expanded",
    );
  };

  const { isRatingSaved, isRatingOngoing } = getStateStatus(cardState);

  useEffect(() => {
    if (isRatingOngoing) {
      dispatch(
        openModal({
          id: "RATING_AND_COMMENT",
          ratingData,
          recipeData: { recipeId, recipeName },
          setUserDefinedState,
          onRatingClicked,
        }),
      );
    } else {
      dispatch(closeModal());
    }
  }, [
    isRatingOngoing,
    dispatch,
    ratingData,
    recipeId,
    recipeName,
    onRatingClicked,
  ]);

  if (!display) {
    return null;
  }

  return (
    <section id={id}>
      <div className="px-2 py-4 mb-2 lg:px-4 bg-background">
        <div className="text-xs">
          <strong>
            {isRatingSaved
              ? intl.THANK_YOU_FOR_RATING
              : intl.GIVE_US_YOUR_RATING}
          </strong>
        </div>
        <button
          onClick={() => {
            setUserDefinedState("ongoing");
          }}
        >
          <RecipeRating
            recipeId={recipeId}
            rating={rating ?? 0}
            allowHover={hasNotInteractedWithRating}
            readOnly={!hasNotInteractedWithRating}
            onRatingClicked={
              hasNotInteractedWithRating ? onRatingClicked : undefined
            }
          />
        </button>
        <RecipeComment
          initialComment={initialComment || ""}
          cardState={cardState}
          isExpanded={cardState === "saved-expanded"}
          onClickAddComment={onClickAddComment}
          onToggleExpanded={onToggleCommentExpanded}
        />
      </div>

      <HaveNotCookedCheckbox
        rating={rating}
        onTickedHaveNotCooked={() => {
          handleTickedHaveNotMadeDish();
          setUserDefinedState(undefined);
        }}
      />
    </section>
  );
};
